import dotenv from "dotenv";
import { z } from "zod";

if (!process.env.NEXT_PUBLIC_ENVIRONMENT && typeof window === "undefined") {
  const dotEnvConfig = dotenv.config();

  if (dotEnvConfig.error) {
    throw new Error("Could not load environment variables");
  }
}

const VALID_ENVIRONMENTS = [
  "local",
  "dev",
  "stage",
  "preprod",
  "prod",
] as const;
const EnvEnum = z.enum(VALID_ENVIRONMENTS);
const environment = EnvEnum.parse(process.env.NEXT_PUBLIC_ENVIRONMENT);
const VALID_APP_MARKETS = ["se", "no", "de"] as const;
const MarketEnum = z.enum(VALID_APP_MARKETS);
const site = MarketEnum.parse(
  process.env.NEXT_PUBLIC_APP_MARKET?.toLowerCase(),
);

/////////////////////////// CONTENTFUL ///////////////////////////
type ContentfulConfig = {
  CONTENTFUL_SPACE_ID: string;
  CONTENTFUL_BRAND_PAGE_TEXT_BLOCK_NAME: string;
  CONTENTFUL_MAGAZINE_DICTIONARY_ID: string;
  CONTENTFUL_CAMPAIGN_DICTIONARY_ID: string;
  CONTENTFUL_CATEGORY_PAGE_DICTIONARY_ID: string;
  CONTENTFUL_GLOBAL_DICTIONARY_ID: string;
  CONTENTFUL_TRADE_PROGRAM_ID: string;
  CONTENTFUL_SPACE_ENVIRONMENT: string;
};

const contentfulConfigCommon = {
  CONTENTFUL_SPACE_ID: "itxkjaj41rzb",
  CONTENTFUL_BRAND_PAGE_TEXT_BLOCK_NAME: "Info box brand pages",
  CONTENTFUL_MAGAZINE_DICTIONARY_ID: "5eakaDD86uoEfYdS9TMzWk",
  CONTENTFUL_CAMPAIGN_DICTIONARY_ID: "3teOmb3bEKVej4IwM3mxhc",
  CONTENTFUL_GLOBAL_DICTIONARY_ID: "AUW4IT51WnUWLjIu1y2XR",
  CONTENTFUL_CATEGORY_PAGE_DICTIONARY_ID: "26pPduK6BYLwGYoCshTMlt",
  CONTENTFUL_TRADE_PROGRAM_ID: "7yzSxZsnyBqTlaHDeyKhwj",
};

const contentfulSpaceEnvironment = getContentfulSpaceEnvironment();

function getContentfulSpaceEnvironment() {
  // Use the process.env.CONTENTFUL_SPACE_ENVIRONMENT for testing and local development purposes only.
  // If not set use the env determined by the environment.
  const { data: contentfulSpaceEnvironment } = z
    .string()
    .safeParse(process.env.CONTENTFUL_SPACE_ENVIRONMENT);

  if (contentfulSpaceEnvironment) {
    return contentfulSpaceEnvironment;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
      return "stage";
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return "master";
  }
}

const contentfulConfig: ContentfulConfig = {
  ...contentfulConfigCommon,
  CONTENTFUL_SPACE_ENVIRONMENT: contentfulSpaceEnvironment,
};

/////////////////////////// DATADOG ///////////////////////////
type DatadogConfig = {
  NEXT_PUBLIC_DATADOG_SITE: string;
  NEXT_PUBLIC_DATADOG_ENV: string;
  NEXT_PUBLIC_DATADOG_RUM: string;
  NEXT_PUBLIC_DATADOG_SAMPLE_RATE: string;
  NEXT_PUBLIC_DATADOG_APPLICATION_ID: string;
  NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: string;
};

function getDataDogEnvironment() {
  const { data: datadogEnvironment } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_DATADOG_ENV);

  if (datadogEnvironment) {
    return datadogEnvironment;
  }

  switch (environment) {
    case EnvEnum.enum.local:
      return "local";
    case EnvEnum.enum.stage:
      return "dev";
    case EnvEnum.enum.preprod:
      return "pre-prod";
    case EnvEnum.enum.prod:
    default:
      return "prod";
  }
}

function getDataDogSampleRate() {
  switch (environment) {
    case EnvEnum.enum.local:
      return "100";
    case EnvEnum.enum.stage:
      return "100";
    case EnvEnum.enum.preprod:
      return "100";
    case EnvEnum.enum.prod:
    default:
      return "1";
  }
}

function getDataDogApplicationId() {
  switch (site) {
    case MarketEnum.enum.se:
      return "e62ac317-bbe1-4ecc-b0b5-c7037044fe34";
    case MarketEnum.enum.no:
      return "3d509390-197e-403c-b4ee-916edfbc3497";
    case MarketEnum.enum.de:
    default:
      return "1c045c58-93da-4b14-9a6c-aa434c011294";
  }
}

function getDataDogClientToken() {
  switch (site) {
    case MarketEnum.enum.se:
      return "puba6a31986623e12840c912c5f3adaf9d1";
    case MarketEnum.enum.no:
      return "pub688ebe5b4ba07acec6f429e5316f305e";
    case MarketEnum.enum.de:
    default:
      return "pubedbd93c8cdafb11d73eb0d796808efb1";
  }
}

function isDatadogRumEnabled() {
  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
      return "false";
    case EnvEnum.enum.prod: {
      const { data: datadogRum } = z
        .string()
        .safeParse(process.env.NEXT_PUBLIC_DATADOG_RUM);

      if (datadogRum) {
        return datadogRum;
      }

      return "false";
    }
    default:
      return "false";
  }
}

const datadogConfig: DatadogConfig = {
  NEXT_PUBLIC_DATADOG_SITE: "datadoghq.eu",
  NEXT_PUBLIC_DATADOG_ENV: getDataDogEnvironment(),
  NEXT_PUBLIC_DATADOG_RUM: isDatadogRumEnabled(),
  NEXT_PUBLIC_DATADOG_SAMPLE_RATE: getDataDogSampleRate(),
  NEXT_PUBLIC_DATADOG_APPLICATION_ID: getDataDogApplicationId(),
  NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: getDataDogClientToken(),
};

/////////////////////////// FEATURE FLAGS ///////////////////////////
type FeatureFlagConfig = {
  NEXT_PUBLIC_FEATURES_SEARCH_ENGINE_EXPLODE_PRODUCTS: string;
  NEXT_PUBLIC_REVIEWS: string;
  NEXT_PUBLIC_FEATURES_CACHE_LOOP: string;
  NEXT_PUBLIC_FEATURES_NEW_MARKET_SELECTOR: string;
  NEXT_PUBLIC_FEATURES_CUSTOMER_SERVICE_IMBOX_BUTTON: string;
  NEXT_PUBLIC_FEATURES_CUSTOMER_SERVICE_EMAIL_LINK: string;
  NEXT_PUBLIC_FEATURES_CUSTOMER_SERVICE_PHONE_LINK: string;
  NEXT_PUBLIC_FEATURES_PRODUCTS_SWATCHES: string;
  NEXT_PUBLIC_FEATURES_PRODUCTS_VIDEOS: string;
  NEXT_PUBLIC_FEATURES_SHOW_DISCOUNT_POPUP: string;
  NEXT_PUBLIC_FEATURES_CHECKOUT_GIFTCARDS: string;
  NEXT_PUBLIC_FEATURES_STATIC_PRODUCT_PAGES: string;
  NEXT_PUBLIC_FEATURES_EXPONENTIAL_PRICE_FILTER: string;
  REDIRECT_ENGINE_ENABLED: string;
  NEXT_PUBLIC_NORDICS_FEATURE_FLAG: string;
  NEXT_PUBLIC_FEATURES_CHECKOUT_GIFTCARDS_CHANGE_BUTTON: string;
  NEXT_PUBLIC_FEATURES_CHECKOUT_MADE_TO_ORDER: string;
  NEXT_PUBLIC_FEATURES_CHECKOUT_ADYEN: string;
  NEXT_PUBLIC_FEATURES_CHECKOUT_WALLEY: string;
  NEXT_PUBLIC_FEATURES_CART_WALLEY_WIDGET: string;
  NEXT_PUBLIC_FEATURES_CHECKOUT_V2: string;
  NEXT_PUBLIC_FEATURES_PRODUCT_NEW_PRODUCT_PAGE: string;
  NEXT_PUBLIC_FEATURES_PRODUCT_CONTENTFUL_PARAMETRICS: string;
  NEXT_PUBLIC_FEATURES_PRODUCT_NEW_GALLERY: string;
  NEXT_PUBLIC_FEATURES_GOOGLE_AUTOMATED_DISCOUNTS: string;
  NEXT_PUBLIC_FEATURES_LINK_PREFETCH: string;
  NEXT_PUBLIC_FEATURES_ELEVATE: string;
  NEXT_PUBLIC_FEATURES_EUROBONUS: string;
  NEXT_PUBLIC_FEATURES_DYNAMIC_PLP: string;
  NEXT_PUBLIC_FEATURES_IMBOX: string;
  NEXT_PUBLIC_FEATURES_FABRIC_SAMPLE: string;
  NEXT_PUBLIC_FEATURES_PRODUCTS_V2_IMAGES: string;
};

function isProductsV2ImagesFeatureFlagEnabled() {
  const { data: productsV2ImagesFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_PRODUCTS_V2_IMAGES);

  if (productsV2ImagesFeatureFlag) {
    return productsV2ImagesFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
      return "true";
    case EnvEnum.enum.prod:
    default:
      return "false";
  }
}

function isImboxFeatureFlagEnabled() {
  const { data: imboxFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_IMBOX);

  if (imboxFeatureFlag) {
    return imboxFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return "true";
  }
}

function isFabricSampleFeatureFlagEnabled() {
  const { data: fabricSampleFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_FABRIC_SAMPLE);

  if (fabricSampleFeatureFlag) {
    return fabricSampleFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
      return "true";
    case EnvEnum.enum.prod:
    default:
      return "false";
  }
}

function isDynamicPlpFeatureFlagEnabled() {
  const { data: dynamicPlpFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_DYNAMIC_PLP);

  if (dynamicPlpFeatureFlag) {
    return dynamicPlpFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return "true";
  }
}

function isEurobonusFeatureFlagEnabled() {
  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return process.env.NEXT_PUBLIC_FEATURES_EUROBONUS ?? "false";
  }
}

function isElevateFeatureFlagEnabled() {
  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return process.env.NEXT_PUBLIC_FEATURES_ELEVATE ?? "true";
  }
}

function isGiftCardFeatureFlagEnabled() {
  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
      return "true";
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return "false";
  }
}

function isAdyenCheckoutFeatureFlagEnabled() {
  switch (site) {
    case MarketEnum.enum.se:
    case MarketEnum.enum.no:
      return "false";
    case MarketEnum.enum.de:
    default:
      const { data: adyenFeatureFlag } = z
        .string()
        .safeParse(process.env.NEXT_PUBLIC_FEATURES_CHECKOUT_ADYEN);

      if (adyenFeatureFlag) {
        return adyenFeatureFlag;
      }

      return "true";
  }
}

function isWalleyCheckoutFeatureFlagEnabled() {
  switch (site) {
    case MarketEnum.enum.se:
    case MarketEnum.enum.de:
    case MarketEnum.enum.no:
      const { data: walleyFeatureFlag } = z
        .string()
        .safeParse(process.env.NEXT_PUBLIC_FEATURES_CHECKOUT_WALLEY);

      if (walleyFeatureFlag) {
        return walleyFeatureFlag;
      }

      return "true";
    default:
      return "false";
  }
}

function isCheckoutV2FeatureFlagEnabled() {
  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
      const { data: checkoutV2FeatureFlag } = z
        .string()
        .safeParse(process.env.NEXT_PUBLIC_FEATURES_CHECKOUT_V2);

      if (checkoutV2FeatureFlag) {
        return checkoutV2FeatureFlag;
      }

      return "true";
    default:
      return "true";
  }
}

function isCartWalleyWidgetFeatureFlagEnabled() {
  const { data: walleyFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_CHECKOUT_WALLEY);

  if (walleyFeatureFlag) {
    return walleyFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
      return "true";
    case EnvEnum.enum.prod:
    default:
      return "false";
  }
}

function isNewProductPageFeatureFlagEnabled() {
  const { data: newProductPageFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_PRODUCT_NEW_PRODUCT_PAGE);

  if (newProductPageFeatureFlag) {
    return newProductPageFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return "false";
  }
}

function isProductContentfulParametricsFeatureFlagEnabled() {
  const { data: productContentfulParametricsFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_PRODUCT_CONTENTFUL_PARAMETRICS);

  if (productContentfulParametricsFeatureFlag) {
    return productContentfulParametricsFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return "true";
  }
}

function isProductNewGalleryFeatureFlagEnabled() {
  const { data: productNewGalleryFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_PRODUCT_NEW_GALLERY);

  if (productNewGalleryFeatureFlag) {
    return productNewGalleryFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return "true";
  }
}

function isgoogleAutomatedDiscountsFeatureFlagEnabled() {
  const { data: googleAutomatedDiscountsFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_GOOGLE_AUTOMATED_DISCOUNTS);

  if (googleAutomatedDiscountsFeatureFlag) {
    return googleAutomatedDiscountsFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
      return "true";
    case EnvEnum.enum.prod:
      return site === MarketEnum.enum.no ? "false" : "true";
    default:
      return "false";
  }
}

function isLinkPrefetchFeatureFlagEnabled() {
  const { data: linkPrefetchFeatureFlag } = z
    .string()
    .safeParse(process.env.NEXT_PUBLIC_FEATURES_LINK_PREFETCH);

  if (linkPrefetchFeatureFlag) {
    return linkPrefetchFeatureFlag;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
    case EnvEnum.enum.preprod:
      return "true";
    case EnvEnum.enum.prod:
    default:
      return "false";
  }
}

const featureFlagConfig: FeatureFlagConfig = {
  NEXT_PUBLIC_FEATURES_SEARCH_ENGINE_EXPLODE_PRODUCTS: "true",
  NEXT_PUBLIC_REVIEWS: "true",
  NEXT_PUBLIC_FEATURES_CACHE_LOOP: "true",
  NEXT_PUBLIC_FEATURES_NEW_MARKET_SELECTOR:
    process.env.NEXT_PUBLIC_FEATURES_NEW_MARKET_SELECTOR ?? "false",
  NEXT_PUBLIC_FEATURES_CUSTOMER_SERVICE_IMBOX_BUTTON: "true",
  NEXT_PUBLIC_FEATURES_CUSTOMER_SERVICE_EMAIL_LINK: "true",
  NEXT_PUBLIC_FEATURES_CUSTOMER_SERVICE_PHONE_LINK: "true",
  NEXT_PUBLIC_FEATURES_PRODUCTS_SWATCHES: "false",
  NEXT_PUBLIC_FEATURES_PRODUCTS_VIDEOS: "true",
  NEXT_PUBLIC_FEATURES_SHOW_DISCOUNT_POPUP:
    process.env.NEXT_PUBLIC_FEATURES_SHOW_DISCOUNT_POPUP ?? "true",
  NEXT_PUBLIC_FEATURES_CHECKOUT_GIFTCARDS: isGiftCardFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_STATIC_PRODUCT_PAGES:
    process.env.NEXT_PUBLIC_FEATURES_STATIC_PRODUCT_PAGES ?? "false",
  NEXT_PUBLIC_FEATURES_EXPONENTIAL_PRICE_FILTER:
    process.env.NEXT_PUBLIC_FEATURES_EXPONENTIAL_PRICE_FILTER ?? "false",
  REDIRECT_ENGINE_ENABLED: process.env.REDIRECT_ENGINE_ENABLED ?? "true",
  NEXT_PUBLIC_NORDICS_FEATURE_FLAG: "true",
  NEXT_PUBLIC_FEATURES_CHECKOUT_GIFTCARDS_CHANGE_BUTTON: "false",
  NEXT_PUBLIC_FEATURES_CHECKOUT_MADE_TO_ORDER:
    process.env.NEXT_PUBLIC_FEATURES_CHECKOUT_MADE_TO_ORDER ?? "false",
  NEXT_PUBLIC_FEATURES_CHECKOUT_ADYEN: isAdyenCheckoutFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_CHECKOUT_WALLEY: isWalleyCheckoutFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_CART_WALLEY_WIDGET:
    isCartWalleyWidgetFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_CHECKOUT_V2: isCheckoutV2FeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_PRODUCT_NEW_PRODUCT_PAGE:
    isNewProductPageFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_PRODUCT_CONTENTFUL_PARAMETRICS:
    isProductContentfulParametricsFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_PRODUCT_NEW_GALLERY:
    isProductNewGalleryFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_GOOGLE_AUTOMATED_DISCOUNTS:
    isgoogleAutomatedDiscountsFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_LINK_PREFETCH: isLinkPrefetchFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_ELEVATE: isElevateFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_EUROBONUS: isEurobonusFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_DYNAMIC_PLP: isDynamicPlpFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_IMBOX: isImboxFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_FABRIC_SAMPLE: isFabricSampleFeatureFlagEnabled(),
  NEXT_PUBLIC_FEATURES_PRODUCTS_V2_IMAGES:
    isProductsV2ImagesFeatureFlagEnabled(),
};

/////////////////////////// CACHE ///////////////////////////
type CacheConfig = {
  USE_REDIS_CACHE: string;
  REVALIDATION_APP_URL: string;
};

const useRedisCache = z.string().parse(process.env.USE_REDIS_CACHE ?? "false");
const cacheConfig: CacheConfig = {
  USE_REDIS_CACHE: useRedisCache,
  REVALIDATION_APP_URL:
    process.env.REVALIDATION_APP_URL ?? "https://prod-revalidate.no-ga.com",
};

/////////////////////////// GRAPH ///////////////////////////
type GraphConfig = {
  NEXT_PUBLIC_SERVICE_ENDPOINT_PATH: string;
  NEXT_PUBLIC_SERVICE_USER: string;
  GRAPHQL_AUTH_USER_V2: string;
  NOGA_API_ENDPOINT: string;
  NGLM_GRAPH_ENDPOINT: string;
  NEXT_PUBLIC_SERVICE_ENDPOINT: string;
};

const graphConfigCommon = {
  NEXT_PUBLIC_SERVICE_ENDPOINT_PATH: "graphql",
  NEXT_PUBLIC_SERVICE_USER: "nglm-graph",
};

const graphConfigLocal: GraphConfig = {
  ...graphConfigCommon,
  GRAPHQL_AUTH_USER_V2: "Admin",
  NOGA_API_ENDPOINT: "https://api2-stage.no-ga.com/api",
  NGLM_GRAPH_ENDPOINT: "http://localhost:4501/nglm-graph/v2",
  NEXT_PUBLIC_SERVICE_ENDPOINT: "http://localhost:4501/nglm-graph/v2",
};

const graphConfigDev: GraphConfig = {
  ...graphConfigCommon,
  GRAPHQL_AUTH_USER_V2: "nglm-graph",
  NOGA_API_ENDPOINT: "https://api2-stage.no-ga.com/api",
  NGLM_GRAPH_ENDPOINT: "https://api2-stage.no-ga.com/nglm-graph/v2",
  NEXT_PUBLIC_SERVICE_ENDPOINT: "https://api2-stage.no-ga.com/nglm-graph/v2",
};

const graphConfigStage: GraphConfig = {
  ...graphConfigCommon,
  GRAPHQL_AUTH_USER_V2: "nglm-graph",
  NOGA_API_ENDPOINT: "https://api2-stage.no-ga.com/api",
  NGLM_GRAPH_ENDPOINT: "https://api2-stage.no-ga.com/nglm-graph/v2",
  NEXT_PUBLIC_SERVICE_ENDPOINT: "https://api2-stage.no-ga.com/nglm-graph/v2",
};

const graphConfigProd: GraphConfig = {
  ...graphConfigCommon,
  GRAPHQL_AUTH_USER_V2: "nglm-graph",
  NOGA_API_ENDPOINT: "https://api2.no-ga.com/api",
  NGLM_GRAPH_ENDPOINT: "https://api2.no-ga.com/nglm-graph/v2",
  NEXT_PUBLIC_SERVICE_ENDPOINT: "https://api2.no-ga.com/nglm-graph/v2",
};

let graphConfig: GraphConfig;
switch (environment) {
  case EnvEnum.enum.local:
    graphConfig = graphConfigLocal;
    break;
  case EnvEnum.enum.dev:
    graphConfig = graphConfigDev;
    break;
  case EnvEnum.enum.stage:
    graphConfig = graphConfigStage;
    break;
  case EnvEnum.enum.preprod:
  case EnvEnum.enum.prod:
  default:
    graphConfig = graphConfigProd;
    break;
}

/////////////////////////// TESTFREAKS ///////////////////////////
type TestfreaksConfig = {
  NEXT_PUBLIC_SE_TESTFREAKS_CLIENT_ID: string;
  NEXT_PUBLIC_NO_TESTFREAKS_CLIENT_ID: string;
  NEXT_PUBLIC_DK_TESTFREAKS_CLIENT_ID: string;
  NEXT_PUBLIC_FI_TESTFREAKS_CLIENT_ID: string;
  NEXT_PUBLIC_EU_TESTFREAKS_CLIENT_ID: string;
};

const testfreaksConfigStage: TestfreaksConfig = {
  NEXT_PUBLIC_SE_TESTFREAKS_CLIENT_ID: "nordiskagalleriet.se-staging",
  NEXT_PUBLIC_NO_TESTFREAKS_CLIENT_ID: "nordiskagalleriet.no-staging",
  NEXT_PUBLIC_DK_TESTFREAKS_CLIENT_ID: "nordiskagalleriet.dk-staging",
  NEXT_PUBLIC_FI_TESTFREAKS_CLIENT_ID: "nordiskagalleriet.fi-staging",
  NEXT_PUBLIC_EU_TESTFREAKS_CLIENT_ID: "no-ga.com-staging",
};

const testfreaksConfigProd: TestfreaksConfig = {
  NEXT_PUBLIC_SE_TESTFREAKS_CLIENT_ID: "nordiskagalleriet.se",
  NEXT_PUBLIC_NO_TESTFREAKS_CLIENT_ID: "nordiskagalleriet.no",
  NEXT_PUBLIC_DK_TESTFREAKS_CLIENT_ID: "nordiskagalleriet.dk",
  NEXT_PUBLIC_FI_TESTFREAKS_CLIENT_ID: "nordiskagalleriet.fi",
  NEXT_PUBLIC_EU_TESTFREAKS_CLIENT_ID: "no-ga.com",
};

const testfreaksConfig: TestfreaksConfig = getTestfreaksConfig();

function getTestfreaksConfig() {
  // Set NEXT_PUBLIC_TESTFREAKS_ENVIRONMENT="prod" in .env to test Testfreaks production account locally.
  const { data: testfreaksEnvironment } = z
    .enum(["stage", "prod"])
    .safeParse(process.env.NEXT_PUBLIC_TESTFREAKS_ENVIRONMENT);

  if (testfreaksEnvironment === "prod") {
    return testfreaksConfigProd;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
      return testfreaksConfigStage;
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return testfreaksConfigProd;
  }
}

/////////////////////////// ADYEN ///////////////////////////
type AdyenConfig = {
  ADYEN_MERCHANT_ACCOUNT: string;
  NEXT_PUBLIC_ADYEN_CLIENT_KEY?: string;
  NEXT_PUBLIC_ADYEN_ENVIRONMENT?: "test" | "live";
  NEXT_PUBLIC_ADYEN_RETURN_URL: string;
  NEXT_PUBLIC_ADYEN_PAYMENT_METHOD_ID: string;
};

const adyenConfigCommon: AdyenConfig = {
  ADYEN_MERCHANT_ACCOUNT: "NOGA_Commerce_ECOM",
  NEXT_PUBLIC_ADYEN_RETURN_URL: "checkout/success?orderRef={ORDERREF}",
  NEXT_PUBLIC_ADYEN_PAYMENT_METHOD_ID: "14",
};

const adyenConfigTest: AdyenConfig = {
  ...adyenConfigCommon,
  NEXT_PUBLIC_ADYEN_CLIENT_KEY: "test_GOOOQLICJJERDETIW5FGPJCKEI5BKBXR",
  NEXT_PUBLIC_ADYEN_ENVIRONMENT: "test",
};

const adyenConfigLive: AdyenConfig = {
  ...adyenConfigCommon,
  NEXT_PUBLIC_ADYEN_CLIENT_KEY: "live_L6JMMVBS5JGUVM6E45K3GCWUKI7IKMYC",
  NEXT_PUBLIC_ADYEN_ENVIRONMENT: "live",
};

const adyenConfig: AdyenConfig = getAdyenConfig();

function getAdyenConfig() {
  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
      return adyenConfigTest;
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return adyenConfigLive;
  }
}

/////////////////////////// WALLEY ///////////////////////////

type WalleyConfig = {
  NEXT_PUBLIC_WALLEY_PAYMENT_METHOD_ID: string;
  NEXT_PUBLIC_WALLEY_RETURN_URL: string;
  NEXT_PUBLIC_WALLEY_SCRIPT_SOURCE: string;
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_SE: number;
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_SE: number;
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_NO: number;
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_NO: number;
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_FI: number;
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_FI: number;
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_DK: number;
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_DK: number;
};

const walleyConfigCommon: WalleyConfig = {
  NEXT_PUBLIC_WALLEY_PAYMENT_METHOD_ID: "165",
  NEXT_PUBLIC_WALLEY_RETURN_URL:
    "checkout/success?reference={checkout.publictoken}",
  NEXT_PUBLIC_WALLEY_SCRIPT_SOURCE:
    "https://api.uat.walleydev.com/walley-checkout-loader.js",
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_SE: 6347,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_SE: 6348,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_NO: 6349,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_NO: 6350,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_FI: 6351,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_FI: 6352,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_DK: 6353,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_DK: 6354,
};

const walleyConfigDev: WalleyConfig = {
  ...walleyConfigCommon,
};

const walleyConfigProd: WalleyConfig = {
  ...walleyConfigCommon,
  NEXT_PUBLIC_WALLEY_SCRIPT_SOURCE:
    "https://api.walleypay.com/walley-checkout-loader.js",
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_SE: 11444,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_SE: 11445,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_NO: 11446,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_NO: 11447,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_FI: 11448,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_FI: 11449,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2C_DK: 11450,
  NEXT_PUBLIC_WALLEY_STORE_CODE_B2B_DK: 11451,
};

const walleyConfig: WalleyConfig = getWalleyConfig();

function getWalleyConfig() {
  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
      return walleyConfigDev;
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
      return walleyConfigProd;
  }
}

/////////////////////////// FIREBASE ///////////////////////////
type FirebaseConfig = {
  NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: string;
  NEXT_PUBLIC_FIREBASE_PROJECT_ID: string;
  NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET: string;
  NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: string;
  NEXT_PUBLIC_FIREBASE_APP_ID: string;
};

const firebaseConfigDev: FirebaseConfig = {
  NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN:
    "nordiska-galleriet-group-dev.firebaseapp.com",
  NEXT_PUBLIC_FIREBASE_PROJECT_ID: "nordiska-galleriet-group-dev",
  NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET:
    "nordiska-galleriet-group-dev.appspot.com",
  NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: "1048732899833",
  NEXT_PUBLIC_FIREBASE_APP_ID: "1:1048732899833:web:704790540de340a6918528",
};

const firebaseConfigProd: FirebaseConfig = {
  NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: "no-ga-576b2.firebaseapp.com",
  NEXT_PUBLIC_FIREBASE_PROJECT_ID: "no-ga-576b2",
  NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET: "no-ga-576b2.appspot.com",
  NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: "389739306215",
  NEXT_PUBLIC_FIREBASE_APP_ID: "1:389739306215:web:ea316a8f2f9e2667205642",
};

let firebaseConfig: FirebaseConfig;
switch (environment) {
  case EnvEnum.enum.local:
  case EnvEnum.enum.dev:
  case EnvEnum.enum.stage:
    firebaseConfig = firebaseConfigDev;
    break;
  case EnvEnum.enum.preprod:
  case EnvEnum.enum.prod:
  default:
    firebaseConfig = firebaseConfigProd;
    break;
}

/////////////////////////// NORCE ///////////////////////////
type NorceConfig = {
  NEXT_PUBLIC_NORCE_IMAGE_CDN: string;
};

const stageCdnUrl = "https://cdn-stage.no-ga.com";
const prodCdnUrl = "https://noga.cdn-norce.tech";
const norceConfigStage: NorceConfig = {
  NEXT_PUBLIC_NORCE_IMAGE_CDN: stageCdnUrl,
};

const norceConfigProd: NorceConfig = {
  NEXT_PUBLIC_NORCE_IMAGE_CDN: prodCdnUrl,
};

let norceConfig: NorceConfig;
switch (environment) {
  case EnvEnum.enum.local:
  case EnvEnum.enum.dev:
  case EnvEnum.enum.stage:
    norceConfig = norceConfigStage;
    break;
  case EnvEnum.enum.preprod:
  case EnvEnum.enum.prod:
  default:
    norceConfig = norceConfigProd;
    break;
}

/////////////////////////// GTM ///////////////////////////
type GtmConfig = {
  GTM_CONTAINER_ID?: string;
};

let gtmConfig: GtmConfig = {
  GTM_CONTAINER_ID: undefined,
};

switch (environment) {
  case EnvEnum.enum.prod:
    gtmConfig = {
      GTM_CONTAINER_ID: "GTM-TQMP4JN",
    };
    break;
  case EnvEnum.enum.stage:
  case EnvEnum.enum.preprod:
    gtmConfig = {
      GTM_CONTAINER_ID: "GTM-NPN3HF89",
    };
    break;
  case EnvEnum.enum.local:
  case EnvEnum.enum.dev:
  default:
    break;
}

/////////////////////////// VOYADO ///////////////////////////
type VoyadoConfig = {
  VOYADO_SCRIPT_BUNDLE?: string;
};

let voyadoConfig: VoyadoConfig = {
  VOYADO_SCRIPT_BUNDLE: undefined,
};

switch (environment) {
  case EnvEnum.enum.local:
  case EnvEnum.enum.dev:
    break;
  case EnvEnum.enum.stage:
  case EnvEnum.enum.preprod:
    voyadoConfig = {
      VOYADO_SCRIPT_BUNDLE:
        "https://assets.voyado.com/jsfiles/analytics_0.1.7.staging.min.js",
    };
    break;
  case EnvEnum.enum.prod:
    voyadoConfig = {
      VOYADO_SCRIPT_BUNDLE:
        "https://assets.voyado.com/jsfiles/analytics_0.1.7.min.js",
    };
    break;
  default:
    break;
}

/////////////////////////// NSHIFT ///////////////////////////
type NShiftConfig = {
  NSHIFT_API_KEY_ID: string;
  NSHIFT_API_URL: string;
  NSHIFT_DELIVERY_CHECKOUT_ID: string;
  NSHIFT_DYNAMIC_FREIGHT_PARTNO: string;
  NEXT_PUBLIC_NSHIFT_DELIVERY_CHECKOUT_ID: string;
  NEXT_PUBLIC_NSHIFT_DYNAMIC_FREIGHT_PARTNO: string;
};

const nshiftConfigCommon = {
  NSHIFT_API_KEY_ID: "4ZDVGM27XVF7HHMF",
  NSHIFT_API_URL: "https://api.unifaun.com/rs-extapi/v1",
  NSHIFT_DELIVERY_CHECKOUT_ID: "598bcef3-a83a-40ff-bc56-3aeab5c18e91",
  NEXT_PUBLIC_NSHIFT_DELIVERY_CHECKOUT_ID:
    "598bcef3-a83a-40ff-bc56-3aeab5c18e91",
};

let freightPartNo: string;
switch (environment) {
  case EnvEnum.enum.local:
  case EnvEnum.enum.dev:
    freightPartNo = "1000027";
    break;
  case EnvEnum.enum.stage:
  case EnvEnum.enum.preprod:
  case EnvEnum.enum.prod:
  default:
    freightPartNo = "1000000";
    break;
}

const nshiftConfig: NShiftConfig = {
  ...nshiftConfigCommon,
  NSHIFT_DYNAMIC_FREIGHT_PARTNO: freightPartNo,
  NEXT_PUBLIC_NSHIFT_DYNAMIC_FREIGHT_PARTNO: freightPartNo,
};

/////////////////////////// SITEMAPS ///////////////////////////
type SitemapConfig = {
  APP_BASE_DOMAIN: string;
  BUILD_APP_SEO: string;
  SITEMAP_NAME: string;
  SITEMAP_BLOB_STORAGE: string;
  SITEMAP_STORAGE_GUID: string;
  /**@deprecated we should move to using market-configurations.ts */
  APP_MARKETS_LOCALES: string;
  SITEMAP_APP_ENVIRONMENT: string; // TODO: remove. Only used for error logging.
  SITEMAP_BASE_DOMAIN: string;
};

const sitemapConfigCommon = {
  BUILD_APP_SEO: "true",
  SITEMAP_BLOB_STORAGE: "nggfeedpublicprod",
  SITEMAP_STORAGE_GUID: "4f11811e-3cbd-4b38-a9bb-634e5f1dffb5",
};

let sitemapName: string;
switch (environment) {
  case EnvEnum.enum.local:
    sitemapName = "localhost";
    break;
  case EnvEnum.enum.preprod:
    sitemapName = "preprod";
    break;
  case EnvEnum.enum.dev:
  case EnvEnum.enum.stage:
  case EnvEnum.enum.prod:
  default:
    sitemapName = "noga";
    break;
}

let appBaseDomain: string;
let sitemapBaseDomain: string;
let appMarketsLocales: string;
switch (site) {
  case MarketEnum.enum.se:
    appBaseDomain = "www.nordiskagalleriet.se";
    sitemapBaseDomain = "nordiska";
    appMarketsLocales = "";
    break;
  case MarketEnum.enum.no:
    appBaseDomain = "www.nordiskagalleriet.no";
    sitemapBaseDomain = "nordiska";
    appMarketsLocales = "";
    break;
  case MarketEnum.enum.de:
  default:
    appBaseDomain = "www.no-ga.com";
    sitemapBaseDomain = "";
    appMarketsLocales =
      "markets=en:de-es-at-fr-be-eu,da:dk,fi:fi,nl:nl&locales=en:eu,da:dk,fi:fi,nl:nl";
    break;
}

const sitemapConfig: SitemapConfig = {
  ...sitemapConfigCommon,
  SITEMAP_APP_ENVIRONMENT: environment,
  SITEMAP_NAME: sitemapName,
  APP_BASE_DOMAIN: appBaseDomain,
  SITEMAP_BASE_DOMAIN: sitemapBaseDomain,
  APP_MARKETS_LOCALES: appMarketsLocales,
};

/////////////////////////// SEARCH ENGINE ///////////////////////////
type ElevateConfig = {
  NEXT_PUBLIC_ELEVATE_CLUSTER_ID: string;
  ELEVATE_CLUSTER_ADMIN_API_KEY?: string;
  PRODUCT_SERVICE_URL: string;
};
let clusterId = "";
let productServiceUrl = "";

switch (environment) {
  case EnvEnum.enum.local:
  case EnvEnum.enum.dev:
  case EnvEnum.enum.stage:
    clusterId = "w09f88952";
    productServiceUrl =
      process.env.PRODUCT_SERVICE_URL ??
      "http://ngg-frontendproductapi-stage-deployment.default.svc.cluster.local:8080/api/frontend-product-api-stage";
    break;
  case EnvEnum.enum.preprod:
  case EnvEnum.enum.prod:
    clusterId = "wDFDC3287";
    productServiceUrl = process.env.PRODUCT_SERVICE_URL ??
      "http://ngg-frontendproductapi-deployment.default.svc.cluster.local:8080/api/frontend-product-api";
    break;
  default:
    break;
}

function getElevateClusterAdminApiKey() {
  const { data } = z
    .string()
    .safeParse(process.env.ELEVATE_CLUSTER_ADMIN_API_KEY);

  if (!data) {
    return undefined;
  }

  return data;
}

const elevateConfig: ElevateConfig = {
  NEXT_PUBLIC_ELEVATE_CLUSTER_ID: clusterId,
  ELEVATE_CLUSTER_ADMIN_API_KEY: getElevateClusterAdminApiKey(),
  PRODUCT_SERVICE_URL: productServiceUrl,
};

/////////////////////////// SEARCH ENGINE ///////////////////////////
type SearchEngineConfig = {
  NEXT_PUBLIC_SEARCH_ENGINE_URL_SE: string;
  NEXT_PUBLIC_SEARCH_ENGINE_URL_NO: string;
  NEXT_PUBLIC_SEARCH_ENGINE_URL_DK: string;
  NEXT_PUBLIC_SEARCH_ENGINE_URL_FI: string;
  NEXT_PUBLIC_SEARCH_ENGINE_URL_NL: string;
  NEXT_PUBLIC_SEARCH_ENGINE_URL: string;
};

const searchEngineConfigDev: SearchEngineConfig = {
  NEXT_PUBLIC_SEARCH_ENGINE_URL_SE:
    "https://nordiskagalleriet-se-dev-new.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL_NO:
    "https://nordiskagalleriet-no-dev-new.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL_DK:
    "https://nordiskagalleriet-dk-dev-new.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL_FI:
    "https://nordiskagalleriet-fi-dev-new.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL_NL:
    "https://nordiskagalleriet-nl-dev-new.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL:
    "https://nordiskagalleriet-en-dev-new.54proxy.com",
};

const searchEngineConfigProd: SearchEngineConfig = {
  NEXT_PUBLIC_SEARCH_ENGINE_URL_SE:
    "https://nordiskagalleriet-se-prod.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL_NO:
    "https://nordiskagalleriet-no-prod.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL_DK:
    "https://nordiskagalleriet-dk-prod.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL_FI:
    "https://nordiskagalleriet-fi-prod.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL_NL:
    "https://nordiskagalleriet-nl-prod.54proxy.com",
  NEXT_PUBLIC_SEARCH_ENGINE_URL:
    "https://nordiskagalleriet-eu-prod.54proxy.com",
};

function getSearchEngineConfig() {
  // With this env variable we can toggle between Loop dev and prod locally and not be dependent on the environment.
  const { data: searchEngineEnvironment } = z
    .enum(["dev", "prod"])
    .safeParse(process.env.NEXT_PUBLIC_SEARCH_ENGINE_ENVIRONMENT);

  if (searchEngineEnvironment === "prod") {
    return searchEngineConfigProd;
  }

  if (searchEngineEnvironment === "dev") {
    return searchEngineConfigDev;
  }

  switch (environment) {
    case EnvEnum.enum.local:
    case EnvEnum.enum.dev:
    case EnvEnum.enum.stage:
      return searchEngineConfigDev;
    case EnvEnum.enum.preprod:
    case EnvEnum.enum.prod:
    default:
      return searchEngineConfigProd;
  }
}

const searchEngineConfig: SearchEngineConfig = getSearchEngineConfig();

switch (site) {
  case MarketEnum.enum.se:
    searchEngineConfig.NEXT_PUBLIC_SEARCH_ENGINE_URL =
      searchEngineConfig.NEXT_PUBLIC_SEARCH_ENGINE_URL_SE;
    break;
  case MarketEnum.enum.no:
    searchEngineConfig.NEXT_PUBLIC_SEARCH_ENGINE_URL =
      searchEngineConfig.NEXT_PUBLIC_SEARCH_ENGINE_URL_NO;
    break;
  case MarketEnum.enum.de:
  default:
    break;
}

/////////////////////////// GENERAL ///////////////////////////
type GeneralConfig = {
  REWRITE_ENDPOINT: string;
  STRING_PLANNER_GUID: string;
  NEXT_PUBLIC_BRAND: string;
  NEXT_PUBLIC_APP_MARKET: string;
  NEXT_PUBLIC_APP_BASE_URL: string;
  NEXT_PUBLIC_GIFT_CARD_PAYMENT_METHOD_ID: string;
  NEXT_PUBLIC_APP_LANGUAGE: string;
  NEXT_PUBLIC_APP_PROTOCOL: string;
  NEXT_PUBLIC_IMGIX_DOMAIN: string;
};

const generalConfigCommon = {
  STRING_PLANNER_GUID: "sit_ODUyMDk5MjAwMA",
  NEXT_PUBLIC_BRAND: z.string().parse(process.env.NEXT_PUBLIC_BRAND),
  NEXT_PUBLIC_APP_MARKET: z.string().parse(process.env.NEXT_PUBLIC_APP_MARKET),
  NEXT_PUBLIC_APP_BASE_URL: z
    .string()
    .parse(process.env.NEXT_PUBLIC_APP_BASE_URL),
  NEXT_PUBLIC_GIFT_CARD_PAYMENT_METHOD_ID: "87",
  NEXT_PUBLIC_IMGIX_DOMAIN: "https://dev-nordiskagalleriet.imgix.net",
};

let appLanguage: string;
let appProtocol: string;
let rewriteEndpoint: string;
switch (site) {
  case MarketEnum.enum.se:
    appLanguage = "sv";
    break;
  case MarketEnum.enum.no:
    appLanguage = "nb";
    break;
  case MarketEnum.enum.de:
  default:
    appLanguage = "en";
    break;
}
switch (environment) {
  case "local": {
    appProtocol = process.env.NEXT_PUBLIC_APP_PROTOCOL || "http";
    rewriteEndpoint = "localhost:3000";
    break;
  }
  case "dev": {
    appProtocol = "https";
    rewriteEndpoint = site === "de" ? "dev.no-ga.com" : `${site}-dev.no-ga.com`;
    break;
  }
  case "stage": {
    appProtocol = "https";
    rewriteEndpoint =
      site === "de" ? "stage.no-ga.com" : `${site}-stage.no-ga.com`;
    break;
  }
  case "preprod": {
    appProtocol = "https";
    rewriteEndpoint =
      site === "de" ? "eu-preprod.no-ga.com" : `${site}-preprod.no-ga.com`;
    break;
  }
  case "prod": {
    appProtocol = "https";
    rewriteEndpoint =
      site === "de" ? "www.no-ga.com" : `www.nordiskagalleriet.${site}`;
    break;
  }
}

const generalConfig: GeneralConfig = {
  ...generalConfigCommon,
  NEXT_PUBLIC_APP_LANGUAGE: appLanguage,
  NEXT_PUBLIC_APP_PROTOCOL: appProtocol,
  REWRITE_ENDPOINT: rewriteEndpoint,
};

/////////////////////////// REDIS ///////////////////////////
type RedisConfig = {
  REDIS_URL?: string;
  REDIS_CACHE?: boolean;
  REDIS_AVAILABLE?: boolean;
};

const redisConfig: RedisConfig = {
  REDIS_URL: z.string().optional().safeParse(process.env.REDIS_URL).data,
  REDIS_CACHE: z.literal("true").safeParse(process.env.REDIS_CACHE).success,
  REDIS_AVAILABLE: z.literal("true").safeParse(process.env.REDIS_AVAILABLE)
    .success,
};

/////////////////////////// EU FLAGS ///////////////////////////
type EuFlagsConfig = {
  HREFLANG_ENABLED: string;
  NEXT_PUBLIC_DK: string;
  NEXT_PUBLIC_FEATURE_SITEMAP: string;
  NEXT_PUBLIC_FI: string;
  NEXT_PUBLIC_NL: string;
  NEXT_PUBLIC_NL_COMING_SOON_PAGE: string;
};

const EuFlagsConfigDefault: EuFlagsConfig = {
  HREFLANG_ENABLED: "false",
  NEXT_PUBLIC_DK: "false",
  NEXT_PUBLIC_FEATURE_SITEMAP: "false",
  NEXT_PUBLIC_FI: "false",
  NEXT_PUBLIC_NL: "false",
  NEXT_PUBLIC_NL_COMING_SOON_PAGE: "false",
};

const EuFlagsConfigEu: EuFlagsConfig = {
  HREFLANG_ENABLED: "true",
  NEXT_PUBLIC_DK: "true",
  NEXT_PUBLIC_FEATURE_SITEMAP: "true",
  NEXT_PUBLIC_FI: "true",
  NEXT_PUBLIC_NL: "true",
  NEXT_PUBLIC_NL_COMING_SOON_PAGE: "true",
};

const euFlagsConfig =
  site === MarketEnum.enum.de ? EuFlagsConfigEu : EuFlagsConfigDefault;

/////////////////////////// ASSEMBLY ///////////////////////////
export type EnvironmentConfig = ContentfulConfig &
  DatadogConfig &
  FeatureFlagConfig &
  CacheConfig &
  GraphConfig &
  TestfreaksConfig &
  AdyenConfig &
  WalleyConfig &
  FirebaseConfig &
  NorceConfig &
  GtmConfig &
  VoyadoConfig &
  NShiftConfig &
  SitemapConfig &
  SearchEngineConfig &
  ElevateConfig &
  GeneralConfig &
  RedisConfig &
  EuFlagsConfig & {
    site: typeof site;
    environment: typeof environment;
  };

export const envConfig: EnvironmentConfig = {
  site,
  environment,
  ...contentfulConfig,
  ...datadogConfig,
  ...featureFlagConfig,
  ...cacheConfig,
  ...graphConfig,
  ...testfreaksConfig,
  ...adyenConfig,
  ...walleyConfig,
  ...firebaseConfig,
  ...norceConfig,
  ...gtmConfig,
  ...voyadoConfig,
  ...nshiftConfig,
  ...sitemapConfig,
  ...searchEngineConfig,
  ...generalConfig,
  ...euFlagsConfig,
  ...elevateConfig,
  ...redisConfig,
};

export const SUPPORTED_LOCALES = [
  "en-de",
  "en-be",
  "en-nl",
  "en-fr",
  "en-es",
  "en-at",
  "eu",
  ...(envConfig.NEXT_PUBLIC_FI === "true" ? ["fi"] : []),
  ...(process.env.NEXT_PUBLIC_EN_FI === "true" ? ["en-fi"] : []),
  ...(envConfig.NEXT_PUBLIC_DK === "true" ? ["dk"] : []),
  ...(process.env.NEXT_PUBLIC_EN_DK === "true" ? ["en-dk"] : []),
  ...(process.env.NEXT_PUBLIC_EN_SE === "true" ? ["en-se"] : []),
  ...(process.env.NEXT_PUBLIC_EN_NO === "true" ? ["en-no"] : []),
  ...(envConfig.NEXT_PUBLIC_NL === "true" ? ["nl"] : []),
];
